import type { PageNumber } from "@pdfking/renderer-api"
import type { TaskContext } from "@pdfking/taskmaker"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import type { MultiPagePdf } from "../types/files/cached/MultiPagePdf"
import type { SinglePagePdf } from "../types/files/cached/SinglePagePdf"
import type { EngineTasks } from "../types/tasks/EngineTasks"
import { PDFMerge } from "@pdfking/renderer-default"
import { SortBy } from "@pdfking/tfunc"
import { GenerateFile } from "../files/GenerateFile"
import { RequestBlob } from "../files/RequestBlob"
import { ContentType } from "../types/files/ContentType"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { TrackedTaskName } from "../types/tasks/TrackedTaskName"

export type MergeInput = {
  readonly content: SinglePagePdf
  readonly page_count: PageNumber
  readonly page_number: PageNumber
}

export const TaskMerge = async function* (
  {
    emit,
    engine,
    options,
  }: TaskContext<
    EngineTasks,
    {
      readonly options: PdfEngineOptions
    }
  >,
  messages: AsyncGenerator<MergeInput>
): AsyncGenerator<MultiPagePdf> {
  const pages: SinglePagePdf[] = []
  let done = 0

  const emitter = (done: number, total: number) =>
    emit({
      done,
      name: TrackedTaskName.MERGE_PDF,
      total,
    })

  void emitter(0, 1)

  async function* pagesGenerator() {
    for await (const { content, page_count, page_number } of messages) {
      void emitter(done, page_count)

      const { size, stream } = await RequestBlob(engine, options, content, emit)

      pages.push(content)

      yield {
        page_number,
        size,
        stream,
        type: ContentType.PDF,
      }

      done += 1
      void emitter(done, page_count)
    }
  }

  const content = await GenerateFile(
    engine,
    options,
    PDFMerge(pagesGenerator()),
    ContentType.PDF,
    emit
  )

  yield {
    content,
    enum: VirtualFileType.MULTI_PAGE_PDF,
    file_meta: {},
    pages: SortBy(pages, p => p.page_number),
  } as MultiPagePdf
}
